import {
  ReportResponseDtoStatus,
} from '@/api/WhisApi/generated';
import { EmptyStateView } from '@/components/EmptyStateView/EmptyStateView';
import { ScreenHeader } from '@/components/ScreenHeader';
import { TabScreenContainer } from '@/components/TabScreenContainer';
import { useAppTranslation } from '@/hooks/useAppTranslation';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { BottomButtonBar } from '@/screens/ReportCreation/components/BottomButtonBar';
import { TextAreaField } from '@/screens/ReportCreation/components/fields/TextAreaField/TextAreaField';
import { useReportSubmission } from '@/screens/ReportCreation/hooks/useReportSubmission';
import { useReportSubmissionNavigation } from '@/screens/ReportCreation/hooks/useReportSubmissionNavigation';
import { useVersionNoteStore } from '@/store/versionNote/versionNote.store';
import { useLocalSearchParams, useRouter, useSegments } from 'expo-router';
import React, { useCallback, useState } from 'react';
import { ScrollView } from 'react-native';
import { getTokens, Separator, SizableText, YStack } from 'tamagui';

export default function VersionNoteScreen(): React.ReactNode {
  const { t } = useAppTranslation();
  const { isSmallScreen } = useBreakpoint();
  const uiTokens = getTokens();
  const router = useRouter();
  const segments = useSegments() as string[];
  const { reportId } = useLocalSearchParams<{ reportId?: string }>();
  const state = useVersionNoteStore((store) => store.state);
  const clearVersionNoteState = useVersionNoteStore((store) => store.clear);
  const [versionNote, setVersionNote] = useState('');

  const isBoardRoute = segments.includes('board');
  const reportEditPathname = isBoardRoute
    ? '/(tabs)/board/reports/[reportId]/edit'
    : '/(tabs)/reports/[reportId]/edit';
  const isPublishedReport =
    state?.report?.status === ReportResponseDtoStatus.published ||
    state?.report?.status === ReportResponseDtoStatus.publishedRRA;
  const { isSubmitting, submitReport } = useReportSubmission({
    reportCode: state?.reportCode,
  });
  const { handleSubmitError, handleSubmissionSuccess, handleQueuedSuccess } =
    useReportSubmissionNavigation({
      isBoardRoute,
      isEditContext: Boolean(reportId),
      editedReportId: reportId,
      onBeforeNavigate: clearVersionNoteState,
    });

  const handleSubmit = useCallback(
    async (mode: 'draft' | 'published'): Promise<void> => {
      if (!state) {
        return;
      }

      const trimmedVersionNote = versionNote.trim();
      if (mode === 'published' && !trimmedVersionNote) {
        return;
      }

      await submitReport(
        state.pendingValues,
        mode,
        {
          onSuccess: (createdReport): void => {
            handleSubmissionSuccess(mode, createdReport.id);
          },
          onQueued: handleQueuedSuccess,
          onError: handleSubmitError,
        },
        {
          versionNote: trimmedVersionNote || undefined,
        },
      );
    },
    [
      handleQueuedSuccess,
      handleSubmissionSuccess,
      handleSubmitError,
      state,
      submitReport,
      versionNote,
    ],
  );

  const handleBackToForm = useCallback((): void => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    if (!reportId) {
      return;
    }

    router.replace({
      pathname: reportEditPathname,
      params: { reportId },
    });
  }, [reportEditPathname, reportId, router]);

  if (!state) {
    return (
      <TabScreenContainer>
        <EmptyStateView title={t('noData')} />
      </TabScreenContainer>
    );
  }

  return (
    <TabScreenContainer>
      <YStack flex={1}>
        {isSmallScreen ? (
          <YStack gap="$true">
            <SizableText size="$h6" fontFamily="$bold">
              {t('reportCreation.versionNote.title')}
            </SizableText>
            <Separator backgroundColor="$color" />
          </YStack>
        ) : (
          <ScreenHeader title={t('reportCreation.versionNote.title')} />
        )}

        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}
          contentContainerStyle={{
            flexGrow: 1,
            paddingVertical: uiTokens.space.$true.val,
          }}
        >
          <YStack gap="$4">
            <TextAreaField
              label={t('reportCreation.versionNote.fieldLabel')}
              placeholder={t('reportCreation.versionNote.placeholder')}
              value={versionNote}
              onValueChange={setVersionNote}
              maxLength={5000}
              required
            />
          </YStack>
        </ScrollView>

        <BottomButtonBar
          showBackButton={!isSmallScreen}
          onBack={handleBackToForm}
          showSaveAsDraft={!isPublishedReport}
          onSaveAsDraft={() => {
            void handleSubmit('draft');
          }}
          primaryButtonLabel={t('reportCreation.buttons.publish')}
          onPrimaryAction={() => {
            void handleSubmit('published');
          }}
          primaryButtonDisabled={!versionNote.trim()}
          primaryButtonLoading={isSubmitting}
        />
      </YStack>
    </TabScreenContainer>
  );
}
